import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { TajweedEvaluation, Session } from '../../types';
import { evaluateRecitation } from '../../api/client';

interface EvaluationState {
  current: TajweedEvaluation | null;
  sessionId: string | null;
  loading: boolean;
  error: string | null;
  history: Session[];
  dailyUsage: number;
  dailyLimit: number;
}

const initialState: EvaluationState = {
  current: null,
  sessionId: null,
  loading: false,
  error: null,
  history: [],
  dailyUsage: 0,
  dailyLimit: 5,
};

export const submitRecitation = createAsyncThunk(
  'evaluation/submitRecitation',
  async ({
    audioBlob,
    surah,
    ayah,
    userLevel,
    language,
  }: {
    audioBlob: Blob;
    surah: number;
    ayah: number;
    userLevel: string;
    language?: string;
  }) => {
    return await evaluateRecitation(audioBlob, surah, ayah, userLevel, language);
  }
);

const evaluationSlice = createSlice({
  name: 'evaluation',
  initialState,
  reducers: {
    clearEvaluation(state) {
      state.current = null;
      state.sessionId = null;
      state.error = null;
    },
    setDailyLimit(state, action: PayloadAction<number>) {
      state.dailyLimit = action.payload;
    },
    resetDailyUsage(state) {
      state.dailyUsage = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitRecitation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitRecitation.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload.evaluation;
        state.sessionId = action.payload.sessionId;
        state.dailyUsage += 1;
        const eval_ = action.payload.evaluation;
        state.history.unshift({
          sessionId: action.payload.sessionId,
          surah: eval_.meta.surah,
          ayah: eval_.meta.ayah,
          overallScore: eval_.overall.score0100,
          confidence: eval_.overall.confidence01,
          transcript: '',
          audioDurationS: 0,
          createdAt: new Date().toISOString(),
        });
      })
      .addCase(submitRecitation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Evaluation failed';
      });
  },
});

export const { clearEvaluation, setDailyLimit, resetDailyUsage } = evaluationSlice.actions;

// Selectors
export const selectCanEvaluate = (state: { evaluation: EvaluationState }) =>
  state.evaluation.dailyLimit <= 0 || state.evaluation.dailyUsage < state.evaluation.dailyLimit;

export default evaluationSlice.reducer;
